import type { OfflineRequest } from "./offline-outbox";

export type RecoveryAction = "pause" | "resume" | "bind-server";
export type RecoveryCommand = { groupId: string; revision: string; action: RecoveryAction; base: string; confirmedServer?: string };
export function queueGroupRevision(items: OfflineRequest[]) {
  return JSON.stringify([...items].sort((a, b) => a.id.localeCompare(b.id)).map(item => [item.id, item.updatedAt, item.status, item.attempts, item.serverBase, item.recovery]));
}
export function groupOfflineRequests(items: OfflineRequest[]) {
  const groups = new Map<string, OfflineRequest[]>();
  for (const item of items) { const rows = groups.get(item.groupId) || []; rows.push(item); groups.set(item.groupId, rows); }
  return [...groups.values()].map(rows => rows.sort((a, b) => a.sequence - b.sequence));
}
export function recoveryBlockReason(items: OfflineRequest[], base: string, allowUnknownServer = false) {
  if (!items.length) return "该组已变化或已补传，请刷新队列";
  for (const item of items) {
    if (item.serverBase === undefined && !allowUnknownServer) return "旧请求缺少服务器归属，请先核对并绑定原服务器";
    if (item.serverBase !== undefined && item.serverBase !== base) return "该组属于其他服务器，请切回原服务器；不能更改已知归属";
    if (/^\/api\/mobile\/worker\/tasks\//.test(item.path)) {
      let body: Record<string, unknown>;
      try { body = JSON.parse(item.body); } catch { return "旧履约请求无法解析，保留原记录并联系管理员核对"; }
      const cycle = body?.expectedAssignmentCycle;
      if (typeof cycle !== "number" || !Number.isSafeInteger(cycle) || cycle < 1) return "旧履约请求缺少可信派工轮次；不能套用当前轮次，请保留原照片并联系管理员核对";
    }
  }
  return "";
}

/** 只改变发送控制及已确认的缺省服务器归属；不改body、照片、请求键和轮次。 */
export function planOfflineRecovery(scopeKey: string, items: OfflineRequest[], command: RecoveryCommand, now = new Date().toISOString()) {
  const group = items.filter(item => item.scopeKey === scopeKey && item.groupId === command.groupId);
  if (!group.length || queueGroupRevision(group) !== command.revision) throw Error("队列已变化，请重新打开处理窗口后核对");
  if (command.action !== "pause" && command.action !== "resume" && command.action !== "bind-server") throw Error("未知队列处理操作");
  if (command.action === "resume") {
    const reason = recoveryBlockReason(group, command.base);
    if (reason) throw Error(reason);
  }
  if (command.action === "bind-server") {
    // 绑定只落已确认的控制元数据，不会发送请求；即使旧履约body缺少轮次，也必须允许先绑定后保持暂停，不能阻断安全迁移。
    if (group.some(item => item.serverBase !== undefined && item.serverBase !== command.base)) throw Error("该组属于其他服务器，请切回原服务器；不能更改已知归属");
    if (!group.some(item => item.serverBase === undefined)) throw Error("该组已有服务器归属，不需要迁移");
    // 同源API以空base存储，但仍要求用户确认当前站点的完整来源，不能空白确认。
    const expected = command.base || globalThis.location?.origin || "";
    if (!expected || command.confirmedServer !== expected) throw Error("请完整输入已核对的原服务器地址，必须与当前服务器一致");
  }
  return group.map(item => ({ ...item,
    ...(command.action === "bind-server" && item.serverBase === undefined ? { serverBase: command.base } : {}),
    status: command.action === "resume" ? "pending" as const : item.status,
    recovery: { paused: command.action !== "resume", changedAt: now, ...(item.recovery?.serverConfirmedAt ? { serverConfirmedAt: item.recovery.serverConfirmedAt } : {}), ...(command.action === "bind-server" ? { serverConfirmedAt: now } : {}) },
    updatedAt: now,
    // 人工处理过的组只在前台发送，防止旧原生镜像重新启用或覆盖。
    backgroundEligible: false
  }));
}

